<script setup lang="ts">
import SearchField from '@/components/filter/SearchField.vue';
import { I18nKey } from '@/utils/typings';
import { QuantityOverviewFilterObject, ScopeFilterObject } from '../types';
import Autocomplete from '@/components/forms/Autocomplete/Autocomplete.vue';
import { SelectItemTypeObject } from '@/components/forms/types';
import { useTranslation } from 'i18next-vue';
import { ScopeProperty, ScopeType, getTranslationPrefix } from '@/utils/translationPrefix';
import {
  MedicalSystemSubCategory,
  QaEquipmentSubCategory,
  RtSystemCategory,
} from '@/api/generator';
import PtwResetFilterButton from '@/components/buttons/PtwResetFilterButton.vue';
import { omit } from 'lodash';

const modelValue = defineModel<QuantityOverviewFilterObject>({
  required: true,
});

const { t } = useTranslation();

function clearFilters() {
  modelValue.value = {};
}

const searchedFieldsI18nKeys: I18nKey[] = ['Name', 'ID', 'Description'];

function getFilterOptions(
  options: string[],
  property: ScopeProperty,
  usage: 'evaluationUsage' | 'setupUsage',
  scope: ScopeType,
): SelectItemTypeObject<ScopeFilterObject>[] {
  return options.map((option) => ({
    title: t(`${getTranslationPrefix(scope, property)}.${option}` as I18nKey),
    value: {
      option,
      property,
      usage,
      scope,
    },
  }));
}

const evaluationUsageOptions: SelectItemTypeObject<ScopeFilterObject>[] = [
  ...getFilterOptions(
    Object.values(omit(RtSystemCategory, ['QaEquipment'])),
    'rtSystemCategory',
    'evaluationUsage',
    'medicalSystemScope',
  ),
  ...getFilterOptions(
    Object.values(QaEquipmentSubCategory),
    'rtSystemSubCategory',
    'evaluationUsage',
    'qaEquipmentScope',
  ),
];

const setupUsageOptions: SelectItemTypeObject<ScopeFilterObject>[] = [
  ...getFilterOptions(
    Object.values(MedicalSystemSubCategory),
    'rtSystemSubCategory',
    'setupUsage',
    'medicalSystemScope',
  ),
  ...getFilterOptions(
    Object.values(QaEquipmentSubCategory),
    'rtSystemSubCategory',
    'setupUsage',
    'qaEquipmentScope',
  ),
];
</script>

<template>
  <v-row class="align-center mt-2 pl-2 pr-2">
    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <SearchField
        v-if="modelValue"
        v-model:modelValue="modelValue.search"
        :placeholder="
          $t('Search', {
            columns: searchedFieldsI18nKeys.map((key) => `$t(${key})`),
          })
        "
      />
    </v-col>

    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <!-- Task Category -->
      <Autocomplete
        v-model="modelValue.evaluationUsage"
        :label="$t('TaskCategory_other')"
        :items="evaluationUsageOptions"
        :placeholder="$t('All')"
        :maxNumDisplayItems="2"
        multiple
        clearable
        chips
        emptyAsUndefined
      />
    </v-col>

    <v-col cols="12" sm="6" md="3" lg="3" class="pa-2">
      <!-- Setup Category -->
      <Autocomplete
        v-model="modelValue.setupUsage"
        :label="$t('SetupCategory_other')"
        :items="setupUsageOptions"
        :placeholder="$t('All')"
        :maxNumDisplayItems="2"
        multiple
        clearable
        chips
        emptyAsUndefined
      />
    </v-col>

    <v-col md="3" lg="1" class="d-flex align-center justify-start pa-2">
      <PtwResetFilterButton @click="clearFilters" />
    </v-col>
  </v-row>
</template>
